import type {
  InstrumentApiResponse,
  ListInstrumentsInputs,
  ListInstrumentsResponse,
} from '@moment/trading-types/instrument-data'
import type {
  AllocationStatisticsRequest,
  AllocationStatisticsResponse,
} from '@/types/api/allocation-statistics'
import type {
  CategoricalBucketsRequest,
  CategoricalBucketsResponse,
} from '@/types/api/categorical-buckets'
import type {
  MaturityBucketPercentileBatchApiResponse,
  MaturityBucketPercentileBatchParams,
  MaturityBucketPercentileBatchResponse,
} from '@/types/api/get-maturity-bucket-data'
import type {
  HistogramBucketsRequest,
  HistogramBucketsResponse,
} from '@/types/api/histogram-buckets'
import type {
  LiveInventoryPostArgs,
  LiveInventoryPostResponse,
} from '@/types/api/live-inventory'
import type {
  GetMarksInputs,
  GetPricesInputs,
  MarksApiResponse,
  PricesApiResponse,
} from '@/types/api/prices'
import type {
  BenchmarkHoldingsRequest,
  BenchmarkHoldingsResponse,
  BondFeaturesRequest,
  BondFeaturesResponse,
  CategoricalRiskFactorRequest,
  CategoricalRiskFactorResponse,
  QuantitativeRiskFactorRequest,
  QuantitativeRiskFactorResponse,
} from '@/types/api/risk-factor'

import { TagTypes } from '@/types/api/tag-types'
import isValidInstrumentIdFormat from '@/utils/instrument'

import { baseApiSlice } from './base-api-slice'

/**
 * Hydrates each batched series into the same shape the single-series endpoint returns, so consumers
 * can treat the two interchangeably. The batch carries `percentile`/`bucket_selection` once; the
 * client-generated `timestamp` is shared by every series in the batch.
 */
function transformMaturityBucketPercentileBatchResponse(
  response: MaturityBucketPercentileBatchApiResponse,
): MaturityBucketPercentileBatchResponse {
  const timestamp = new Date().toISOString()
  const series = Object.fromEntries(
    Object.entries(response.series ?? {}).map(([key, result]) => [
      key,
      {
        bucket_selection: response.bucket_selection,
        percentile: response.percentile,
        count: result.count,
        data: result.data,
        timestamp,
      },
    ]),
  )

  return {
    bucket_selection: response.bucket_selection,
    percentile: response.percentile,
    series,
  }
}

const dataSlice = baseApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getInstrument: builder.query<InstrumentApiResponse, string>({
      query: (id) => ({
        url: `data/instrument/${encodeURIComponent(id)}/`,
        method: 'GET',
      }),
      providesTags: [TagTypes.Instrument],
    }),
    // This is a query, rather than a mutation, since the latter does not accept providesTags -- only invalidatesTags
    getLiveInventoryPost: builder.query<
      LiveInventoryPostResponse,
      LiveInventoryPostArgs
    >({
      query: ({
        filters,
        returns,
        page,
        page_size,
        trade_size,
        risk_group_id,
        sort_by,
        side,
        use_close_data_after_hours,
        objective_function,
        objective_side,
        federal_tax_rate,
        state_tax_rate,
        residing_state,
        tax_profile,
        custom_income_tax,
        custom_capital_gains_tax,
      }) => ({
        url: `data/inventory/`,
        body: {
          // TODO: Remove this default once the UI supports multi-asset class selection.
          // Callers can override by passing their own asset_type filter.
          filters: {
            asset_type: { equal_to: 'fixed_income' },
            ...filters,
          },
          returns,
          page,
          page_size,
          trade_size,
          risk_group_id,
          sort_by,
          side,
          use_close_data_after_hours: use_close_data_after_hours ?? true,
          ...(objective_function && { objective_function }),
          ...(objective_side && { objective_side }),
          // Investor tax profile (PORT-2531). Listed directly rather than
          // conditionally spread: JSON serialization drops undefined keys, and
          // a truthiness guard would wrongly drop a valid 0 rate.
          federal_tax_rate,
          state_tax_rate,
          residing_state,
          tax_profile,
          custom_income_tax,
          custom_capital_gains_tax,
        },
        method: 'POST',
      }),
      providesTags: (_result, _error, arg) =>
        arg.returns.includes('filter_lists')
          ? [TagTypes.LiveInventory, TagTypes.LiveInventoryFilterLists]
          : [TagTypes.LiveInventory],
    }),
    listInstruments: builder.query<
      ListInstrumentsResponse,
      ListInstrumentsInputs
    >({
      query: ({ status, page, page_size }) => ({
        url: `data/instrument/`,
        method: 'GET',
        params: {
          status,
          page,
          page_size,
        },
      }),
      providesTags: [TagTypes.Instrument],
    }),
    getPrices: builder.query<PricesApiResponse, GetPricesInputs>({
      query: ({ instrumentID, frequency, start, end }) => ({
        url: `data/instrument/${instrumentID}/price/`,
        params: {
          frequency,
          start,
          end,
        },
      }),
      providesTags: [TagTypes.Price],
    }),
    getMarks: builder.query<MarksApiResponse[], GetMarksInputs>({
      query: ({ instruments }) => ({
        url: `data/marks/`,
        params: {
          instruments: instruments.join(','),
        },
      }),
      providesTags: [TagTypes.Mark],
    }),

    getMaturityBucketPercentileBatch: builder.query<
      MaturityBucketPercentileBatchResponse,
      MaturityBucketPercentileBatchParams
    >({
      query: ({ risk_group_id, percentile, series }) => ({
        url: `data/maturity-bucket/percentile/batch/`,
        method: 'POST',
        body: {
          risk_group_id,
          percentile,
          series,
        },
      }),
      transformResponse: transformMaturityBucketPercentileBatchResponse,
      providesTags: [TagTypes.LiveInventory],
    }),

    postAllocationStatistics: builder.query<
      AllocationStatisticsResponse,
      AllocationStatisticsRequest
    >({
      query: ({ instrument_ids }) => ({
        url: `data/allocation-statistics/`,
        method: 'POST',
        body: {
          instrument_ids,
        },
      }),
      providesTags: [TagTypes.AllocationStatistics],
    }),

    postCategoricalRiskFactor: builder.query<
      CategoricalRiskFactorResponse,
      CategoricalRiskFactorRequest
    >({
      query: ({
        filters,
        field,
        holdings,
        mode,
        limit,
        risk_group_id,
        fund_isin,
        side,
        valuation_config,
      }) => ({
        url: `data/categorical-holdings/`,
        method: 'POST',
        body: {
          filters,
          field,
          holdings,
          mode,
          limit,
          risk_group_id,
          fund_isin,
          side,
          valuation_config,
        },
      }),
      providesTags: [TagTypes.LiveInventory],
    }),

    postQuantitativeRiskFactor: builder.query<
      QuantitativeRiskFactorResponse,
      QuantitativeRiskFactorRequest
    >({
      query: ({
        filters,
        field,
        holdings,
        buckets,
        risk_group_id,
        fund_isin,
        side,
        valuation_config,
      }) => ({
        url: `data/histogram-holdings/`,
        method: 'POST',
        body: {
          filters,
          field,
          holdings,
          buckets,
          risk_group_id,
          fund_isin,
          side,
          valuation_config,
        },
      }),
      providesTags: [TagTypes.LiveInventory],
    }),

    postCategoricalBuckets: builder.query<
      CategoricalBucketsResponse,
      CategoricalBucketsRequest
    >({
      query: ({
        field,
        filters,
        page,
        page_size,
        trade_size,
        side,
        risk_group_id,
        use_close_data_after_hours,
      }) => ({
        url: `data/categorical-buckets/`,
        method: 'POST',
        body: {
          field,
          filters,
          page,
          page_size,
          trade_size,
          side,
          risk_group_id,
          use_close_data_after_hours,
        },
      }),
      providesTags: [TagTypes.LiveInventory],
    }),

    postHistogramBuckets: builder.query<
      HistogramBucketsResponse,
      HistogramBucketsRequest
    >({
      query: ({
        field,
        filters,
        trade_size,
        side,
        risk_group_id,
        use_close_data_after_hours,
        bucket_boundaries,
        bucket_count,
      }) => ({
        url: `data/histogram-buckets/`,
        method: 'POST',
        body: {
          field,
          filters,
          trade_size,
          side,
          risk_group_id,
          use_close_data_after_hours,
          bucket_boundaries,
          bucket_count,
        },
      }),
      providesTags: [TagTypes.LiveInventory],
    }),

    postBondFeatures: builder.query<BondFeaturesResponse, BondFeaturesRequest>({
      query: ({
        filters,
        holdings,
        features,
        risk_group_id,
        fund_isin,
        side,
        valuation_config,
      }) => ({
        url: `data/bond-features/`,
        method: 'POST',
        body: {
          filters,
          holdings,
          features,
          risk_group_id,
          fund_isin,
          side,
          valuation_config,
        },
      }),
      providesTags: [TagTypes.LiveInventory],
    }),

    postBenchmarkHoldings: builder.query<
      BenchmarkHoldingsResponse,
      BenchmarkHoldingsRequest
    >({
      query: ({ fund_isin, investment_amount }) => ({
        url: `data/benchmark-holdings/`,
        method: 'POST',
        body: {
          fund_isin,
          investment_amount,
        },
      }),
      providesTags: [TagTypes.LiveInventory],
    }),
  }),
})

export const {
  endpoints: {
    getInstrument,
    getMarks,
    getPrices,
    listInstruments,
    getLiveInventoryPost,
    getMaturityBucketPercentileBatch,
    postAllocationStatistics,
    postCategoricalRiskFactor,
    postQuantitativeRiskFactor,
    postCategoricalBuckets,
    postHistogramBuckets,
    postBondFeatures,
    postBenchmarkHoldings,
  },
  useGetInstrumentQuery,
  useGetPricesQuery,

  useGetLiveInventoryPostQuery,
  useLazyGetLiveInventoryPostQuery,
  useGetMaturityBucketPercentileBatchQuery,
  usePostAllocationStatisticsQuery,
  usePostCategoricalRiskFactorQuery,
  usePostQuantitativeRiskFactorQuery,
  usePostCategoricalBucketsQuery,

  usePostBondFeaturesQuery,
  usePostBenchmarkHoldingsQuery,
} = dataSlice

// Consumed only by useInstrumentMarks below — kept unexported so knip doesn't
// flag it as an unused export.
const { useGetMarksQuery } = dataSlice

export const useInstrumentMarks = (isin: string | null | undefined) => {
  const normalizedIsin = isin || ''
  const isValid = isValidInstrumentIdFormat(normalizedIsin)

  // Fetch price data only if normalizedIsin is available
  return useGetMarksQuery(
    { instruments: [normalizedIsin] },
    { skip: !normalizedIsin || !isValid },
  )
}
